import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import type { TipoPerfil } from '../types/models';
import { isPasskeyAvailableOnThisDevice, normalizePasskeyEmail } from './passkeyDevice';
import { AcBootLoader } from '../components/shared/AcBootLoader';

interface ProtectedRouteProps {
  /** Perfis aceitos nesta rota. Se vazio, qualquer autenticado pode acessar. */
  allowedPerfis?: TipoPerfil[];
  /** Rota de redirecionamento para não-autenticados */
  redirectTo?: string;
  children?: React.ReactNode;
  /** Se true, não aplicar redirect de onboarding nesta rota */
  skipOnboarding?: boolean;
}

/**
 * Componente de proteção de rotas.
 * - Redireciona para /login se não autenticado
 * - Redireciona para /onboarding/welcome no PRIMEIRO acesso (ultimoLogin === null)
 * - Redireciona para dashboard do perfil se perfil não permitido
 */
export function ProtectedRoute({ allowedPerfis, redirectTo = '/login', children, skipOnboarding }: ProtectedRouteProps) {
  const { isAuthenticated, user: rawUser, isLoading, isImpersonating } = useAuthStore();
  const location = useLocation();
  // Ainda carregando auth — não redireciona
  if (isLoading) return <AcBootLoader />;

  // Não autenticado → login com redirect param
  if (!isAuthenticated || !rawUser) {
    const searchParams = new URLSearchParams();
    searchParams.set('redirect', location.pathname + location.search);
    const targetUrl = `${redirectTo}?${searchParams.toString()}`;
    console.warn('[ProtectedRoute] Usuário não autenticado. Redirecionando para:', targetUrl);
    return <Navigate to={targetUrl} replace />;
  }

  const user = rawUser;

  const isSimRoute = location.pathname.startsWith('/simulador') || location.pathname.startsWith('/dev');
  // Embed do cockpit (iframe same-origin) ou impersonate — não aplicar redirects de "senha → /simulador"
  const isSimulating = isSimRoute ||
                       (new URLSearchParams(location.search).get('impersonate_receiver') === 'true' || sessionStorage.getItem('ac_impersonate') === 'true') ||
                       sessionStorage.getItem('ac_cockpit_embed') === '1' ||
                       !!(user as any)?.isSimulation ||
                       (typeof window !== 'undefined' && window.self !== window.top);
  const isDemoUser = (user as any).isDemo === true ||
                     (user.email && (user.email.endsWith('@sim.demo.ac.br') || user.email.endsWith('@alunoconectado.com.br')));

  // Login só com passcode do simulador → restringe ao /simulador, EXCETO iframes do cockpit
  // (sem isso o JWT isDemo no embed redireciona para /simulador e remonta o cockpit em loop).
  const authMethod = sessionStorage.getItem('ac_auth_method');
  const isRealUserOrAdmin = user && (user.perfil === 'ADMIN' || !(user as any).isDemo);
  if (
    authMethod === 'password' &&
    !isRealUserOrAdmin &&
    !isSimRoute &&
    !isSimulating &&
    !location.pathname.startsWith('/security') &&
    !location.pathname.startsWith('/onboarding') &&
    !location.pathname.startsWith('/install')
  ) {
    console.warn('[ProtectedRoute] Acesso restrito para login com senha. Redirecionando para /simulador');
    return <Navigate to="/simulador" replace />;
  }

  // ── Bloqueio PWA Mandatório no celular ─────────────────────────────
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';
  const isImpersonateReceiver = new URLSearchParams(location.search).get('impersonate_receiver') === 'true' || 
                                sessionStorage.getItem('ac_impersonate') === 'true';

  if (isMobile && !isStandalone && !skippedPwaInstall && !isImpersonateReceiver && sessionStorage.getItem('ac_force_browser') !== 'true') {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/install?redirect=${encodeURIComponent(redirect)}`} replace />;
  }




  // Primeiro acesso (ultimoLogin === null) → redirecionar para onboarding
  // Não redirecionar se já está em /onboarding, /simulador, /security ou é demo/impersonation
  // Não redirecionar se acessoDireto (provisionado diretamente, pula onboarding)
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const isSystemRoute = location.pathname.startsWith('/simulador') || location.pathname.startsWith('/security');
  const isDemo = (user as any).isDemo;
  const isAcessoDireto = (user as any).acessoDireto;
  const isEscolaSemSenha = user.perfil === 'ESCOLA' && (user as any).hasSenha === false;

  // Regra de ativação: usuário ESCOLA sem senha não pode navegar no sistema
  // antes de definir a senha ou concluir o setup em /admin/setup.
  if (isEscolaSemSenha && !location.pathname.startsWith('/ativar') && !location.pathname.startsWith('/admin/setup')) {
    console.warn('[ProtectedRoute] Usuário ESCOLA sem senha. Redirecionando para /admin/setup', { path: location.pathname });
    return <Navigate to="/admin/setup" replace />;
  }

  if (!skipOnboarding && !isOnboardingRoute && !isSystemRoute && !isDemo && !isAcessoDireto) {
    // Campo ultimoLogin === null significa que nunca houve login anterior
    if ('ultimoLogin' in user && user.ultimoLogin === null) {
      console.warn('[ProtectedRoute] Primeiro login detectado. Redirecionando para onboarding específico', { path: location.pathname, perfil: user.perfil });
      if (user.perfil === 'ESCOLA' && (user.cargo === 'DIRETOR' || !user.cargo)) {
        return <Navigate to="/onboarding/escola/diretor" replace />;
      }
      if (user.perfil === 'PROFESSOR') {
        return <Navigate to="/onboarding/professor" replace />;
      }
      return <Navigate to="/onboarding/security" replace />;
    }

    // Fluxo obrigatório de segurança pendente (setado na ativação) ou conta adm sem passkey ativa
    const storedSecurityPending = localStorage.getItem('ac_security_onboarding_pending') === '1';
    const isAdminPerfil = ['ADMIN', 'ESCOLA', 'ESTADUAL', 'MUNICIPAL', 'REDE', 'PROFESSOR'].includes(user.perfil);
    const passKeyDevices = user.passKeyDevices || [];
    const normalizedUserEmail = normalizePasskeyEmail(user.email);
    const hasLocalKey = isPasskeyAvailableOnThisDevice(user.email);
    const verifiedDesktopPasskeyEmail = normalizePasskeyEmail(
      sessionStorage.getItem('ac_security_desktop_passkey_verified'),
    );
    const hasVerifiedDesktopPasskeyThisSession =
      verifiedDesktopPasskeyEmail === normalizedUserEmail;
    const hasServerMobileKey = passKeyDevices.includes('mobile');
    const hasServerDesktopKey = passKeyDevices.includes('desktop');
    const hasMobileBiometric = user.biometriaAtiva === true || hasServerMobileKey;
    const hasDesktopPasskeyOnThisDevice =
      hasLocalKey && (hasServerDesktopKey || hasVerifiedDesktopPasskeyThisSession);
    const hasSecurityCredentialForThisContext = hasMobileBiometric || hasDesktopPasskeyOnThisDevice;
    const skippedLegacyDevice = localStorage.getItem('ac_security_onboarding_skipped_device') === '1';
    if (skippedLegacyDevice && normalizedUserEmail) {
      localStorage.setItem(`ac_security_onboarding_skipped_${normalizedUserEmail}`, '1');
      localStorage.removeItem('ac_security_onboarding_skipped_device');
    }
    const skippedOnThisAccount = normalizedUserEmail
      ? localStorage.getItem(`ac_security_onboarding_skipped_${normalizedUserEmail}`) === '1' || skippedLegacyDevice
      : false;
    const BYPASS_SECURITY_COMPLIANCE = isSimulating || isDemoUser;

    const securityPending = !BYPASS_SECURITY_COMPLIANCE && storedSecurityPending && !hasSecurityCredentialForThisContext;
    const securityRequired = !BYPASS_SECURITY_COMPLIANCE &&
                             isAdminPerfil &&
                             !hasSecurityCredentialForThisContext &&
                             !isImpersonating &&
                             !skippedOnThisAccount;

    if (!BYPASS_SECURITY_COMPLIANCE && !skippedOnThisAccount && (securityPending || securityRequired)) {
      console.warn('[ProtectedRoute] Onboarding de segurança pendente ou obrigatório para perfil adm. Redirecionando para /onboarding/security', {
        path: location.pathname,
        securityPending,
        securityRequired
      });
      return <Navigate to="/onboarding/security" replace />;
    }
  }

  // Verificar perfil
  const impersonating = isImpersonating || (typeof window !== 'undefined' && (!!sessionStorage.getItem('ac_impersonate') || !!sessionStorage.getItem('ac_impersonatedByName')));
  
  console.log('[ProtectedRoute Debug]', {
    path: location.pathname,
    perfilUsuario: user.perfil,
    allowedPerfis,
    impersonating,
    permitido: allowedPerfis && allowedPerfis.length > 0 ? allowedPerfis.includes(user.perfil) : true
  });

  if (allowedPerfis && allowedPerfis.length > 0 && !allowedPerfis.includes(user.perfil)) {
    if (isSimulating || isDemoUser) {
      // Bypass all role blockings locally for presentation in simulation
    } else {
      const perfilRoutes: Record<string, string> = {
        ADMIN: '/master/dashboard',
        ESCOLA: '/admin/dashboard',
        PROFESSOR: '/professor/dashboard',
        ALUNO: '/aluno/dashboard',
        RESPONSAVEL: '/responsavel/dashboard',
        MUNICIPAL: '/municipal/dashboard',
        ESTADUAL: '/estadual/dashboard',
        REDE: '/rede/dashboard',
        PUBLICO: '/public/dashboard',
      };
      let dest = perfilRoutes[user.perfil] || '/login';
      // Coordenação/Secretaria (ESCOLA + cargo) → equipe dashboard
      if (user.perfil === 'ESCOLA' && (user.cargo === 'COORDENACAO' || user.cargo === 'SECRETARIA')) {
        dest = '/equipe/dashboard';
      }
      console.warn('[ProtectedRoute] Perfil não autorizado. Redirecionando para rota padrão:', dest, {
        path: location.pathname,
        perfilUsuario: user.perfil,
        allowedPerfis
      });
      return <Navigate to={dest} replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
}
